import type { ComponentType } from 'react';
import { FormattedMessage } from 'react-intl';

import Compliance from 'assets/images/icons/Compliance.svg?react';
import {
    BarChart,
    BriefcaseIcon,
    BuildingIcon,
    HouseIcon,
    InboxIcon,
    SquareCheckIcon,
    TimerIcon,
} from 'components/icons';
import { ADMIN_ITEMS, ADMIN_ROOT } from 'pages/admin/adminItems';
import type { NavItemType } from 'types/menu';
import { cn } from 'utils/cn';
import { type FamilyId, familyTileClass } from 'utils/families';
import { PIN_KINDS } from 'utils/pins';
import getClassicItems from './other';

/**
 * Classic's links by id. Classic hides a group's links with the group, so a link with no gate of its
 * own takes its group's.
 */
// ponytail: a per-field fallback, not an AND of both gates. It is exact while every gated classic link
// repeats its group's gate or sits in an ungated group, which `newDesign.test.tsx` checks.
function classicLinks(): Map<string, NavItemType> {
    const links = new Map<string, NavItemType>();
    for (const entry of getClassicItems().children ?? []) {
        for (const link of entry.children ?? [entry]) {
            links.set(link.id!, {
                ...link,
                permissions: link.permissions ?? entry.permissions,
                requiredFeature: link.requiredFeature ?? entry.requiredFeature,
                disabled: link.disabled || entry.disabled,
            });
        }
    }
    return links;
}

type Glyph = ComponentType<{ className?: string }>;

/** A section's mark as its icon: the tile Pinned marks a record with, sized by the caller. The title names it. */
const sectionMark = (Glyph: Glyph, family: FamilyId): NavItemType['icon'] =>
    function SectionMark({ className }: { className?: string }) {
        return (
            <span
                aria-hidden
                className={cn(
                    'flex shrink-0 items-center justify-center rounded-[5px]',
                    familyTileClass(family),
                    className
                )}
            >
                <Glyph className="size-3" />
            </span>
        );
    };

const section = (name: string, titleId: string, family: FamilyId, icon: Glyph, links: NavItemType[]): NavItemType => ({
    id: `section-${name}`,
    type: 'collapse',
    title: <FormattedMessage id={titleId} />,
    icon: sectionMark(icon, family),
    children: links,
});

/**
 * The Admin entry's gate. A menu entry shows with any one of its `permissions`, an Admin page with all of its
 * own, so the entry takes the codes that open a page alone: every page asking for more also asks for one of them.
 */
const adminPermissions = (): string[] => [
    ...new Set(ADMIN_ITEMS.filter((item) => item.permissions.length === 1).flatMap((item) => item.permissions)),
];

/**
 * The new design's menu: My work, then seven sections under the Firm heading, then Admin. Each link is
 * classic's entry, gate included, so it shows exactly when classic shows it, and keeps classic's id. Inbox
 * and Admin are the entries of its own.
 *
 * A section is marked with the glyph its first list's records pin under, read from the pins' own table, on its
 * family's tile. Compliance and Reports hold no record that pins and take the classic menu's glyphs.
 */
const getNewDesignItems = (): NavItemType[] => {
    const classic = classicLinks();
    const link = (id: string, change?: Partial<NavItemType>): NavItemType => ({ ...classic.get(id), ...change });

    return [
        {
            id: 'my-work',
            type: 'group',
            children: [
                link('dashboard', { id: 'home', title: <FormattedMessage id="menu.home" />, icon: HouseIcon }),
                link('timecards', { id: 'time', title: <FormattedMessage id="menu.time" />, icon: TimerIcon }),
                // No url and no classic counterpart: the sidebar opens it as the Inbox drawer.
                { id: 'inbox', type: 'item', title: <FormattedMessage id="menu.inbox" />, icon: InboxIcon },
                link('tasks', { icon: SquareCheckIcon }),
                link('my-portfolio', { icon: BriefcaseIcon }),
            ],
        },
        {
            id: 'firm',
            type: 'group',
            title: <FormattedMessage id="menu.firm" />,
            children: [
                section('cases', 'matters.matters', 'cases', PIN_KINDS.matter.icon, [
                    // Without `/new-cases`: that list belongs to Reports › New cases here.
                    link('cases', { altActiveUrls: ['/matter'] }),
                    link('projects'),
                    link('delegations'),
                    link('correspondence'),
                ]),
                section('contacts', 'menu.contacts', 'contacts', PIN_KINDS.contact.icon, [
                    link('contacts'),
                    link('groups'),
                    link('contracts'),
                    link('marketing-profiles'),
                ]),
                section('business-development', 'menu.business-development', 'bd', PIN_KINDS.effort.icon, [
                    link('efforts'),
                    link('experience-records'),
                    link('law-firms'),
                    link('debriefings'),
                    link('rankings-referees'),
                ]),
                section('compliance', 'menu.compliance', 'compliance', Compliance, [
                    link('coi-checks'),
                    link('compliance-overview'),
                ]),
                section('people', 'menu.people', 'people', PIN_KINDS.employee.icon, [
                    link('colleagues'),
                    link('teams'),
                    link('offices'),
                    link('practice-areas'),
                    link('trainings'),
                ]),
                section('finance', 'menu.finance', 'finance', PIN_KINDS.invoice.icon, [
                    link('invoices'),
                    link('billableclients'),
                    link('pricelists'),
                    link('expenses'),
                    link('banktransfers'),
                    link('prepayments'),
                    link('client-assets'),
                    link('hbr'),
                    link('erp'),
                ]),
                section('reports', 'menu.reports', 'reports', BarChart, [
                    link('reports'),
                    link('reporting'),
                    link('open-advances'),
                    link('open-invoices'),
                    link('unpaid-invoices'),
                    link('sales-journal'),
                    link('foreign-sales'),
                    // Classic has no entry for it, only the Cases list's tab, so it takes the Cases link's gate.
                    link('cases', {
                        id: 'new-cases',
                        title: <FormattedMessage id="menu.new-cases" />,
                        url: '/matters/new-cases',
                        altActiveUrls: undefined,
                    }),
                    link('trainings-reports'),
                ]),
            ],
        },
        // A group of its own, so Admin stays the menu's last entry whatever the Firm sections hold.
        {
            id: 'admin-area',
            type: 'group',
            children: [
                {
                    id: 'admin',
                    type: 'item',
                    title: <FormattedMessage id="menu.admin" />,
                    url: ADMIN_ROOT,
                    icon: BuildingIcon,
                    permissions: adminPermissions(),
                },
            ],
        },
    ];
};

export default getNewDesignItems;
